import { MouseEvent, useMemo, useState } from 'react';

import LayersClearIcon from '@mui/icons-material/LayersClear';
import TimerIcon from '@mui/icons-material/Timer';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import find from 'lodash-es/find';
import map from 'lodash-es/map';
import orderBy from 'lodash-es/orderBy';
import { useTranslation } from 'react-i18next';

import {
  DashboardPageToolbar,
  DashboardToolbarActions,
  DashboardToolbarAside,
  DashboardToolbarHeading,
} from 'src/common/components/DashboardPageToolbar';
import PageBox from 'src/common/components/PageBox';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { ticketCardGridItemSx, ticketCardGridSize } from 'src/common/styles/ticketCardStyles';
import { LanguageKeys } from 'src/common/constants/languages';
import { checkForAllWords } from 'src/helpers/search';
import {
  ECheckinFilters,
  ECheckinTimerType,
  IBulkCheckinTimer,
  ICheckin,
} from 'src/interfaces/Checkin';
import { ICompetition } from 'src/interfaces/Competition';
import { EOrder } from 'src/interfaces/Order';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import { EView } from 'src/interfaces/View';
import CompetitionFilterForm from 'src/pages/Dashboard/Competitions/forms/CompetitionFilterForm';
import CompetitionTable from 'src/pages/Dashboard/Competitions/tables/CompetitionTable';
import { SER } from 'src/common/constants/participantSortKeys';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import CheckinBulkActionsModal, { BulkActionOptions } from './CheckinBulkActionsModal';
import CheckinCard from './CheckinCard';

interface Props {
  checkins: ICheckin[];
  competitionId: string;
  distanceId: string;
  onClickBulkTimer: (
    payload: Omit<IBulkCheckinTimer, 'editionId'>,
    options?: BulkActionOptions,
  ) => void;
  onClickTimer: (id: string, type: ECheckinTimerType, timeStamp?: Date) => void;
  onSetDnf: (id: string, dnf: boolean) => void;
  onDeleteCheckin: (checkinId: string, e?: MouseEvent<unknown>) => void;
  onEditCheckin: (checkinId: string, e?: MouseEvent<unknown>) => void;
  onResetCounter: (distanceId: string, e?: MouseEvent<unknown>) => void;
  onSetView: (v: EView) => void;
  view: EView;
  isCurrentEdition?: boolean;
  isSignupOpen?: boolean;
}

const CheckinDistance = ({
  checkins = [],
  competitionId,
  distanceId,
  onClickBulkTimer,
  onClickTimer,
  onSetDnf,
  onDeleteCheckin,
  onEditCheckin,
  onResetCounter,
  onSetView,
  view,
  isCurrentEdition = true,
  isSignupOpen = false,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { competitions, distances } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );

  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [timerStartedFilter, setTimerStartedFilter] = useState<string | null>(null);
  const [timerFinishedFilter, setTimerFinishedFilter] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');
  const [order, setOrder] = useState<EOrder>(EOrder.asc);
  const [orderByKey, setOrderByKey] = useState<string>(SER);

  const competition = find(competitions, c => c.id === competitionId) as ICompetition;
  const competitionName = competition.i18n[currentLang];

  const distance = find(distances, d => d.id === distanceId) as TechnicalType;
  const distanceName = distance.i18n[currentLang];

  const sortedCheckins = useMemo(() => {
    const filtered = checkins.filter(c => {
      const matchesQuery = !query ||
        (c.participant &&
          (checkForAllWords(query, `${c.participant.firstName}${c.participant.lastName}`) ||
            !!checkForAllWords(query, c.participant.email) ||
            !!checkForAllWords(query, c.participant.phoneNumber)));
      const matchesTimerStarted = !timerStartedFilter ||
        (timerStartedFilter === 'true' ? !!c.startTime :
          timerStartedFilter === 'false' ? !c.startTime : true);
      const matchesTimerFinished = !timerFinishedFilter ||
        (timerFinishedFilter === 'true' ? !!c.finishTime :
          timerFinishedFilter === 'false' ? !c.finishTime : true);

      return matchesQuery && matchesTimerStarted && matchesTimerFinished;
    });

    return orderBy(filtered, orderByKey, order);
  }, [checkins, query, timerStartedFilter, timerFinishedFilter, orderByKey, order]);

  const selectedCheckinIds = useMemo(() => {
    return sortedCheckins.map(c => c.id);
  }, [sortedCheckins]);

  const onChangeFilters = (key: ECheckinFilters, value: string | null) => {
    switch (key) {
      case ECheckinFilters.timerStarted:
        setTimerStartedFilter(value);
        break;
      case ECheckinFilters.timerFinished:
        setTimerFinishedFilter(value);
        break;
      default:
        break;
    }
  };

  const canResetSerialNumbers = isCurrentEdition && isSignupOpen;
  const resetSerialTooltip = !isCurrentEdition
    ? t('timers.bulkActionsDisabledNotCurrentEdition')
    : !isSignupOpen
      ? t('counters.resetDisabledSignupsClosed')
      : t('counters.resetAllCounters');

  const handleBulkTimerClick = (
    type: ECheckinTimerType,
    timeStamp?: Date,
    options?: BulkActionOptions,
  ) => {
    onClickBulkTimer(
      {
        id: competitionId,
        distanceId,
        type,
        checkinIds: selectedCheckinIds,
        timeStamp,
      },
      options,
    );
  };

  return (
    <>
      <DashboardPageToolbar collapseOnMobile={false}>
        <DashboardToolbarHeading>
          <Typography variant="h6" component="div">
            {`${t('checkins.header', {
              competition: competitionName,
              distance: distanceName,
            })} (${sortedCheckins.length})`}
          </Typography>
        </DashboardToolbarHeading>
        <DashboardToolbarActions>
          {sortedCheckins.length ? (
            <Tooltip
              title={
                isCurrentEdition ? '' : t('timers.bulkActionsDisabledNotCurrentEdition')
              }
              disableHoverListener={isCurrentEdition}
              disableFocusListener={isCurrentEdition}
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TimerIcon />}
                  aria-label={t('timers.bulkActions')}
                  disabled={!isCurrentEdition}
                  onClick={() => setBulkActionsOpen(true)}
                >
                  {t('timers.bulkActions')}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title={resetSerialTooltip}>
            <span>
              <IconButton
                aria-label={t('counters.resetAllCounters')}
                disabled={!canResetSerialNumbers}
                onClick={e => onResetCounter(distanceId, e)}
              >
                <LayersClearIcon />
              </IconButton>
            </span>
          </Tooltip>
        </DashboardToolbarActions>
        <DashboardToolbarAside variant="grow">
          <CompetitionFilterForm
            filter={query}
            onChange={onChangeFilters}
            onSetView={onSetView}
            order={order}
            orderByKey={orderByKey}
            setFilter={setQuery}
            setOrder={setOrder}
            setOrderByKey={setOrderByKey}
            timerFinishedFilter={timerFinishedFilter}
            timerStartedFilter={timerStartedFilter}
            view={view}
          />
        </DashboardToolbarAside>
      </DashboardPageToolbar>
      <CheckinBulkActionsModal
        open={bulkActionsOpen}
        handleClose={() => setBulkActionsOpen(false)}
        participantCount={sortedCheckins.length}
        onStartAll={options => handleBulkTimerClick(ECheckinTimerType.start, undefined, options)}
        onSetAllStartTimes={(timeStamp, options) =>
          handleBulkTimerClick(ECheckinTimerType.start, timeStamp, options)
        }
        onStopAll={options => handleBulkTimerClick(ECheckinTimerType.finish, undefined, options)}
        onSetAllFinishTimes={(timeStamp, options) =>
          handleBulkTimerClick(ECheckinTimerType.finish, timeStamp, options)
        }
        onResetAllTimers={options => handleBulkTimerClick(ECheckinTimerType.clear, undefined, options)}
      />
      <PageBox>
        {sortedCheckins.length ? (
          <>
            {view === EView.grid ? (
              <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                {map(sortedCheckins, checkin => (
                  <Grid
                    key={checkin.id}
                    size={ticketCardGridSize}
                    sx={ticketCardGridItemSx}
                  >
                    <CheckinCard
                      checkin={checkin}
                      competition={competition}
                      distanceLabel={distanceName}
                      currentLang={currentLang}
                      onClickTimer={onClickTimer}
                      onSetDnf={onSetDnf}
                      onDeleteCheckin={onDeleteCheckin}
                      onEditCheckin={onEditCheckin}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : null}
            {view === EView.table ? (
              <CompetitionTable
                checkins={sortedCheckins}
                distanceId={distanceId}
                onClickTimer={onClickTimer}
                onSetDnf={onSetDnf}
                onDeleteCheckin={onDeleteCheckin}
                onEditCheckin={onEditCheckin}
              />
            ) : null}
          </>
        ) : (
          <Paper
            sx={{
              ...frostedPanelSx(0.5),
              width: '100%',
              mb: 2,
              mt: 2,
              p: 2,
            }}
          >
            <Typography variant="body1" component="div" sx={{ mr: 1 }}>
              {t('checkins.noCheckinsMessage', {
                competition: competition.i18n[currentLang],
                distance: distance.i18n[currentLang],
              })}
            </Typography>
          </Paper>
        )}
      </PageBox>
    </>
  );
};

export default CheckinDistance;
